import React from 'react';
import { Tag } from 'antd';
import {
    DeliveryTime,
    DeliveryTimeCategory,
    DeliveryTimeOtherEntry,
    formatRange,
    isEmptyDeliveryTime,
} from '../utils/deliveryTime';

interface Props {
    value: DeliveryTime;
}

/**
 * 配送时间结构化展示 (只读, 用于详情查看模式)。
 *
 * <p>跟 {@link DeliveryTimeEditor} 对应, 把 parse 出来的 schema 渲染成读起来舒服的列表。
 */
const DeliveryTimeView: React.FC<Props> = ({ value }) => {
    if (isEmptyDeliveryTime(value)) {
        return <span className="text-xs text-gray-400">无</span>;
    }
    return (
        /*
         * CSS Grid 两列 - label (工作日/休息日/...) 右对齐, value (tag + remark) 左对齐,
         * 中间只剩 gap-x-3 一个间距, 不再被旧版 flex justify-between 撑出大空白.
         */
        <div
            className="grid gap-x-3 gap-y-1.5 items-baseline text-sm"
            style={{ gridTemplateColumns: 'max-content 1fr' }}
        >
            <CategoryRow title="工作日" category={value.workday} />
            <CategoryRow title="休息日" category={value.restday} />
            <CategoryRow title="节假日" category={value.holiday} />
            {value.other.length > 0 && (
                <>
                    {/*
                     * "其他" 分组分隔线 - 跨两列 (col-span-2), 跟上面 3 类视觉分开;
                     * 浅色细字 + 顶部细分隔线, 不抢戏.
                     */}
                    <div className="col-span-2 text-xs text-gray-400 pt-1.5 mt-1 border-t border-gray-100">其他</div>
                    {value.other.map((e, idx) => (
                        <OtherEntryRow key={idx} entry={e} />
                    ))}
                </>
            )}
        </div>
    );
};

/**
 * "其他" 自定义日期 entry - 跟 CategoryRow 共享父级 grid 的两列, 左侧名字, 右侧时段 + 备注.
 * 不再用 `border-l-2 pl-2` 卡片样式. 名字 font-medium 因为是用户自定义内容 (不是固定类目 label).
 */
const OtherEntryRow: React.FC<{ entry: DeliveryTimeOtherEntry }> = ({ entry }) => (
    <>
        <span className="text-gray-700 font-medium">{entry.name || '(未命名)'}</span>
        <div className="flex flex-col gap-0.5">
            <RangesList ranges={entry.ranges} />
            {entry.remark && <div className="text-xs text-gray-500">{entry.remark}</div>}
        </div>
    </>
);

/**
 * 固定类目 row (工作日 / 休息日 / 节假日) - 输出两个 grid cell:
 * 左 cell = title (右对齐到 label 列右缘), 右 cell = ranges + remark (左对齐).
 */
const CategoryRow: React.FC<{ title: string; category: DeliveryTimeCategory }> = ({ title, category }) => {
    const hasContent = category.ranges.length > 0 || !!category.remark;
    if (!hasContent) {
        return (
            <>
                <span className="text-gray-500">{title}</span>
                <span className="text-gray-400">-</span>
            </>
        );
    }
    return (
        <>
            <span className="text-gray-500">{title}</span>
            <div className="flex flex-col gap-0.5">
                <RangesList ranges={category.ranges} />
                {category.remark && <div className="text-xs text-gray-500">{category.remark}</div>}
            </div>
        </>
    );
};

const RangesList: React.FC<{ ranges: DeliveryTime['workday']['ranges'] }> = ({ ranges }) => {
    if (ranges.length === 0) return <span className="text-gray-400 text-xs">无时段</span>;
    // 左对齐 - 跟 grid 父级 value 列保持一致; 多个 tag 之间用 gap-1 留空隙;
    // tag 自己关掉默认 margin (m-0), 让 gap 控制间距.
    return (
        <div className="flex flex-wrap gap-1">
            {ranges.map((r, idx) => (
                <Tag key={idx} className="m-0">{formatRange(r)}</Tag>
            ))}
        </div>
    );
};

export default DeliveryTimeView;
